import React, { useState, useEffect } from 'react';

// Design Tokens
export const COLORS = {
  primary: {
    light: '#22d3ee',
    DEFAULT: '#06b6d4',
    dark: '#0891b2',
  },
  success: {
    light: '#34d399',
    DEFAULT: '#10b981',
    dark: '#059669',
  },
  warning: {
    light: '#fbbf24',
    DEFAULT: '#f59e0b',
    dark: '#d97706',
  },
  error: {
    light: '#f87171',
    DEFAULT: '#ef4444',
    dark: '#dc2626',
  },
  gray: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
  }
};

// Field Limits
export const FIELD_LIMITS = {
  name: { max: 100, min: 2 },
  email: { max: 100 },
  password: { max: 50, min: 8 },
  mobile: { max: 10, min: 10 },
  title: { max: 150, min: 10 },
  goalAmount: { max: 10000000, min: 2000 },
  patientName: { max: 100, min: 2 },
  medicalCondition: { max: 200 },
  hospital: { max: 150 },
  city: { max: 100 },
  story: { max: 5000, min: 50 },
};

// Validation Regex
export const REGEX = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  password: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/,
  mobile: /^[0-9]{10}$/,
};

// Shared Animation Config
export const ANIMATION = {
  duration: {
    fast: 200,
    normal: 300,
    slow: 500,
    entrance: 1000,
  },
  easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
};

// Auto-save Hook
export const useAutoSave = (formData, formName, delay = 3000) => {
  const [lastSaved, setLastSaved] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (!formData || Object.keys(formData).length === 0) return;

    const timer = setTimeout(async () => {
      try {
        setIsSaving(true);
        setSaveError(null);
        
        const draftData = {
          data: formData,
          timestamp: new Date().toISOString(),
          formName,
          version: '1.0',
        };
        
        localStorage.setItem(`draft_${formName}`, JSON.stringify(draftData));
        
        await new Promise(resolve => setTimeout(resolve, 500));
        
        setLastSaved(new Date());
      } catch (error) {
       
        setSaveError('Failed to auto-save draft');
      } finally {
        setIsSaving(false);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [formData, formName, delay]);

  const loadDraft = () => {
    try {
      const saved = localStorage.getItem(`draft_${formName}`);
      if (saved) {
        const draft = JSON.parse(saved);
        return draft.data;
      }
    } catch (error) {
     
    }
    return null;
  };

  const clearDraft = () => {
    localStorage.removeItem(`draft_${formName}`);
    setLastSaved(null);
  };

  return { lastSaved, isSaving, saveError, loadDraft, clearDraft };
};

// Step Indicator Component
export const StepIndicator = ({ currentStep, totalSteps = 3, stepLabels = ['Purpose', 'Details', 'Contact'] }) => {
  return (
    <div className="flex items-center justify-center mb-10">
      {Array.from({ length: totalSteps }, (_, index) => (
        <React.Fragment key={index}>
          <div className="flex flex-col items-center">
            <div className={`
              w-11 h-11 rounded-full flex items-center justify-center 
              text-base font-semibold transition-all duration-300
              ${currentStep === index + 1 
                ? 'bg-gradient-to-br from-gsg-teal to-gsg-teal-dark text-white shadow-lg shadow-gsg-teal/30' 
                : currentStep > index + 1 
                ? 'bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-md' 
                : 'bg-gray-100 text-gray-400 border border-gray-300'
              }
            `}>
              {currentStep > index + 1 ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                index + 1
              )}
            </div>
            <span className="text-sm mt-3 font-medium text-gray-700">
              {stepLabels[index]}
            </span>
          </div>
          {index < totalSteps - 1 && (
            <div className={`h-1 w-20 mx-4 rounded-full ${currentStep > index + 1 
              ? 'bg-gradient-to-r from-green-500 to-emerald-600' 
              : 'bg-gray-200'
            }`}></div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// Primary Button Component
export const PrimaryButton = ({ 
  children, 
  onClick, 
  isLoading = false, 
  disabled = false, 
  className = '',
  type = 'button'
}) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled || isLoading}
    className={`
      px-8 py-3.5 bg-gradient-to-r from-gsg-teal to-gsg-teal-dark 
      text-white rounded-xl hover:shadow-lg hover:shadow-gsg-teal/25 
      disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:shadow-none
      transition-all duration-300 font-semibold text-base flex items-center justify-center
      focus:outline-none focus:ring-2 focus:ring-gsg-teal focus:ring-offset-2
      ${className}
    `}
  >
    {isLoading ? (
      <>
        <svg className="animate-spin h-5 w-5 mr-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Processing...
      </>
    ) : children}
  </button>
);

// Secondary Button Component
export const SecondaryButton = ({ children, onClick, className = '' }) => (
  <button
    onClick={onClick}
    className={`
      px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl 
      hover:bg-gray-50 hover:border-gray-400 hover:text-gray-900
      transition-all duration-300 font-medium text-base
      focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2
      ${className}
    `}
  >
    {children}
  </button>
);

// Input Field Component
export const InputField = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  type = 'text',
  placeholder,
  error,
  required = false,
  maxLength,
  disabled = false,
  className = '',
  showCharacterCounter = false,
}) => {
  const [characterCount, setCharacterCount] = useState(value?.length || 0);

  const handleChange = (e) => {
    const newValue = e.target.value;
    if (maxLength && newValue.length > maxLength) return;
    setCharacterCount(newValue.length);
    onChange(e);
  };

  return (
    <div className="mb-5">
      <label htmlFor={name} className="block text-sm font-semibold text-gray-800 mb-2.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={handleChange}
        onBlur={onBlur}
        disabled={disabled}
        maxLength={maxLength}
        placeholder={placeholder}
        className={`
          w-full px-4 py-3.5 rounded-xl border-2 transition-all duration-300
          focus:outline-none focus:ring-2 focus:ring-gsg-teal/30 focus:border-gsg-teal
          placeholder:text-gray-400 text-gray-900
          ${error 
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
            : 'border-gray-200 hover:border-gray-300'
          }
          ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
          ${className}
        `}
      />
      <div className="flex justify-between items-center mt-2">
        {error && (
          <p className="text-sm text-red-600 flex items-center">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </p>
        )}
        {showCharacterCounter && maxLength && (
          <div className={`text-xs ml-auto ${characterCount > maxLength * 0.9 ? 'text-amber-600' : 'text-gray-500'}`}>
            {characterCount}/{maxLength}
          </div>
        )}
      </div>
    </div>
  );
};

// Textarea Component
export const TextareaField = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  error,
  required = false,
  maxLength,
  rows = 4,
  showCharacterCounter = true,
}) => {
  const [characterCount, setCharacterCount] = useState(value?.length || 0);

  const handleChange = (e) => {
    const newValue = e.target.value;
    if (maxLength && newValue.length > maxLength) return;
    setCharacterCount(newValue.length);
    onChange(e);
  };

  return (
    <div className="mb-5">
      <label htmlFor={name} className="block text-sm font-semibold text-gray-800 mb-2.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <textarea
        id={name}
        name={name}
        value={value}
        onChange={handleChange}
        onBlur={onBlur}
        rows={rows}
        maxLength={maxLength}
        placeholder={placeholder}
        className={`
          w-full px-4 py-3.5 rounded-xl border-2 transition-all duration-300
          focus:outline-none focus:ring-2 focus:ring-gsg-teal/30 focus:border-gsg-teal
          placeholder:text-gray-400 text-gray-900 resize-none
          ${error 
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
            : 'border-gray-200 hover:border-gray-300'
          }
        `}
      />
      <div className="flex justify-between items-center mt-2">
        {error && (
          <p className="text-sm text-red-600 flex items-center">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </p>
        )}
        {showCharacterCounter && maxLength && (
          <div className={`text-xs ml-auto ${characterCount > maxLength * 0.9 ? 'text-amber-600' : 'text-gray-500'}`}>
            {characterCount}/{maxLength}
          </div>
        )}
      </div>
    </div>
  );
};

// Select Field Component
export const SelectField = ({
  label,
  name,
  value,
  onChange,
  options,
  error,
  required = false,
  placeholder = "Select an option",
}) => (
  <div className="mb-5">
    <label htmlFor={name} className="block text-sm font-semibold text-gray-800 mb-2.5">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <select
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      className={`
        w-full px-4 py-3.5 rounded-xl border-2 transition-all duration-300
        focus:outline-none focus:ring-2 focus:ring-gsg-teal/30 focus:border-gsg-teal
        text-gray-900 bg-white
        ${error 
          ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
          : 'border-gray-200 hover:border-gray-300'
        }
      `}
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
    {error && (
      <p className="mt-2 text-sm text-red-600 flex items-center">
        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        {error}
      </p>
    )}
  </div>
);

// Error Message Component
export const ErrorMessage = ({ message, type = 'error' }) => {
  const config = {
    error: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-700',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    warning: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-700',
      icon: '⚠️',
    },
    info: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-700',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    success: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      text: 'text-emerald-700',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
        </svg>
      ),
    },
  };

  const { bg, border, text, icon } = config[type];

  return (
    <div className={`p-4 ${bg} ${border} border rounded-xl mb-6`}>
      <div className="flex items-center space-x-3">
        <div className={`flex-shrink-0 ${text}`}>{icon}</div>
        <p className={`text-sm font-medium ${text}`}>{message}</p>
      </div>
    </div>
  );
};

// Loading Overlay Component
export const LoadingOverlay = ({ message = "Processing..." }) => (
  <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white/90 backdrop-blur-sm">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-gsg-teal/30 border-t-gsg-teal rounded-full animate-spin mx-auto mb-6"></div>
      <p className="text-gray-800 font-semibold text-lg">{message}</p>
      <p className="text-sm text-gray-600 mt-3">Please wait while we process your request</p>
    </div>
  </div>
);

// Success Modal Component
export const SuccessModal = ({ 
  isOpen, 
  onClose, 
  title = "Success!", 
  message = "Operation completed successfully.",
  showCountdown = false,
  countdownDuration = 3,
}) => {
  const [countdown, setCountdown] = useState(countdownDuration);

  useEffect(() => {
    if (!isOpen || !showCountdown) return;

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          onClose?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, showCountdown, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl animate-scale-in">
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          
          <h3 className="text-2xl font-bold text-gray-900 mb-3">{title}</h3>
          
          <p className="text-gray-600 mb-8">{message}</p>
          
          {showCountdown && (
            <div className="mb-6">
              <p className="text-sm text-gray-500 mb-3">
                Redirecting in {countdown} second{countdown !== 1 ? 's' : ''}...
              </p>
              <div className="flex items-center justify-center space-x-2">
                <div className="w-2 h-2 bg-gsg-teal rounded-full animate-pulse"></div>
                <div className="w-2 h-2 bg-gsg-teal rounded-full animate-pulse delay-150"></div>
                <div className="w-2 h-2 bg-gsg-teal rounded-full animate-pulse delay-300"></div>
              </div>
            </div>
          )}
          
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-gradient-to-r from-gsg-teal to-gsg-teal-dark text-white rounded-xl hover:shadow-lg hover:shadow-gsg-teal/25 transition-all font-medium"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

// Auto-save Indicator Component
export const AutoSaveIndicator = ({ lastSaved, isSaving, saveError }) => (
  <div className="fixed top-4 right-4 z-40">
    {isSaving && (
      <div className="flex items-center space-x-2 bg-white px-4 py-2.5 rounded-xl shadow-lg border border-gray-200 animate-fade-in">
        <svg className="animate-spin h-4 w-4 text-gsg-teal" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span className="text-sm text-gray-700 font-medium">Saving...</span>
      </div>
    )}
    
    {lastSaved && !isSaving && !saveError && (
      <div className="flex items-center space-x-2 bg-white px-4 py-2.5 rounded-xl shadow-lg border border-gray-200 animate-fade-in">
        <svg className="h-4 w-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
        </svg>
        <span className="text-sm text-gray-700">
          Saved {lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    )}
    
    {saveError && (
      <div className="flex items-center space-x-2 bg-red-50 px-4 py-2.5 rounded-xl shadow-lg border border-red-200 animate-fade-in">
        <svg className="h-4 w-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className="text-sm text-red-600 font-medium">{saveError}</span>
      </div>
    )}
  </div>
);

// Password Requirements Component
export const PasswordRequirements = ({ password }) => {
  const requirements = [
    { 
      label: 'At least 8 characters', 
      met: password.length >= 8,
      required: true 
    },
    { 
      label: 'Contains uppercase letter', 
      met: /[A-Z]/.test(password),
      required: true 
    },
    { 
      label: 'Contains lowercase letter', 
      met: /[a-z]/.test(password),
      required: true 
    },
    { 
      label: 'Contains number', 
      met: /\d/.test(password),
      required: true 
    },
    { 
      label: 'Special character (optional)', 
      met: /[^A-Za-z0-9]/.test(password),
      required: false 
    },
  ];

  const metRequirements = requirements.filter(req => req.met).length;
  const totalRequired = requirements.filter(req => req.required).length;

  return (
    <div className="mt-4 space-y-2.5">
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium text-gray-700">Password strength</span>
        <span className="text-sm font-semibold text-gray-900">
          {metRequirements}/{totalRequired} requirements met
        </span>
      </div>
      
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div 
          className="bg-gradient-to-r from-red-400 via-yellow-400 to-green-500 h-2 rounded-full transition-all duration-500"
          style={{ width: `${(metRequirements / totalRequired) * 100}%` }}
        ></div>
      </div>
      
      <div className="space-y-2 mt-3">
        {requirements.map((req, index) => (
          <div key={index} className="flex items-center">
            <div className={`w-5 h-5 rounded-full mr-3 flex items-center justify-center flex-shrink-0 ${
              req.met 
                ? 'bg-emerald-100 text-emerald-600' 
                : req.required 
                  ? 'bg-red-100 text-red-600' 
                  : 'bg-gray-100 text-gray-400'
            }`}>
              {req.met ? '✓' : req.required ? '!' : '○'}
            </div>
            <span className={`text-sm ${
              req.met 
                ? 'text-emerald-700 font-medium' 
                : req.required 
                  ? 'text-red-600' 
                  : 'text-gray-500'
            }`}>
              {req.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Form Card Component
export const FormCard = ({ title, icon, children, footer }) => {
  const Icon = icon;
  
  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 max-w-md mx-auto">
      {/* Header */}
      <div className="px-8 py-8 border-b border-gray-100">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-gradient-to-br from-gsg-teal to-gsg-teal-dark rounded-xl flex items-center justify-center shadow-md">
            {typeof icon === 'string' ? (
              <span className="text-xl text-white">{icon}</span>
            ) : (
              <Icon className="w-6 h-6 text-white" />
            )}
          </div>
          <div>
            <h3 className="text-2xl font-bold text-gray-900">{title}</h3>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="px-8 py-8">
        {children}
      </div>
      
      {/* Footer */}
      {footer && (
        <div className="px-8 py-6 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          {footer}
        </div>
      )}
    </div>
  );
};

// Feature Card Component
export const FeatureCard = ({ title, description, icon, gradient, isActive, index }) => (
  <div className={`
    p-5 bg-white/95 backdrop-blur-sm rounded-2xl border-2 transition-all duration-500 
    ${isActive 
      ? 'border-gsg-teal/30 shadow-xl shadow-gsg-teal/10 scale-[1.02]' 
      : 'border-gray-100 shadow-sm hover:border-gray-200'
    }
  `}>
    <div className="flex items-start space-x-4">
      <div className={`
        w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md
        bg-gradient-to-br ${gradient}
      `}>
        <span className="text-2xl">{icon}</span>
      </div>
      <div>
        <div className="flex items-center mb-1.5">
          <span className="text-xs font-semibold text-gsg-teal bg-gsg-teal/10 px-2 py-1 rounded mr-2">
            #{index + 1}
          </span>
          <h4 className="font-bold text-gray-900 text-lg">{title}</h4>
        </div>
        <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  </div>
);

// Left Section Component
export const LeftSection = ({ 
  title, 
  subtitle, 
  features = [], 
  currentFeature = 0,
  showFeatures = true 
}) => {
  const [isAnimated, setIsAnimated] = useState(false);

  useEffect(() => {
    setTimeout(() => setIsAnimated(true), 300);
  }, []);

  return (
    <div className="hidden lg:flex lg:w-2/5 relative bg-gradient-to-br from-gsg-teal/5 via-white to-white">
      <div className="relative z-10 flex flex-col justify-center items-start w-full h-full p-12">
        <div className="max-w-lg">
          {/* Logo & Title */}
          <div className={`mb-12 transition-all duration-1000 ${isAnimated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-gsg-teal to-gsg-teal-dark rounded-2xl mb-8 shadow-2xl shadow-gsg-teal/20 animate-pulse-slow">
              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            
            <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
              {title.split(' ').map((word, idx) => (
                <span 
                  key={idx} 
                  className={word.toLowerCase().includes('fundraiser') || 
                            word.toLowerCase().includes('mobile') || 
                            word.toLowerCase().includes('complete') 
                    ? 'text-gsg-teal' 
                    : ''
                  }
                >
                  {word}{' '}
                </span>
              ))}
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed">
              {subtitle}
            </p>
          </div>
          
          {/* Features */}
          {showFeatures && (
            <div className={`transition-all duration-1000 delay-300 ${isAnimated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="space-y-5">
                {features.map((feature, index) => (
                  <FeatureCard
                    key={index}
                    title={feature.title}
                    description={feature.description}
                    icon={feature.icon}
                    gradient={feature.gradient}
                    isActive={currentFeature === index}
                    index={index}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// OTP Input Component
export const OTPInput = ({ otp, setOtp, error, onComplete }) => {
  const inputRefs = Array(6).fill(0).map(() => React.createRef());

  const handleChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs[index + 1].current.focus();
    }

    if (newOtp.every(digit => digit) && onComplete) {
      onComplete(newOtp.join(''));
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs[index - 1].current.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text').slice(0, 6).split('');
    if (pasteData.every(digit => /^\d$/.test(digit))) {
      const newOtp = [...otp];
      pasteData.forEach((digit, index) => {
        if (index < 6) {
          newOtp[index] = digit;
        }
      });
      setOtp(newOtp);
      if (newOtp.every(digit => digit) && onComplete) {
        onComplete(newOtp.join(''));
      }
    }
  };

  return (
    <div className="mb-6">
      <label className="block text-sm font-semibold text-gray-800 mb-4">
        6-digit OTP <span className="text-red-500">*</span>
      </label>
      <div className="flex justify-between space-x-3 mb-4" onPaste={handlePaste}>
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={inputRefs[index]}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={digit}
            onChange={(e) => handleChange(e.target.value, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onFocus={() => inputRefs[index].current.select()}
            className={`
              w-14 h-14 text-center text-2xl font-bold rounded-xl border-2 
              transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-gsg-teal/30
              ${error 
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                : digit 
                  ? 'border-gsg-teal bg-gsg-teal/5' 
                  : 'border-gray-200 hover:border-gray-300'
              }
            `}
            maxLength="1"
            autoComplete="one-time-code"
          />
        ))}
      </div>
      {error && (
        <p className="text-sm text-red-600 flex items-center">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </p>
      )}
    </div>
  );
};